import React, { ReactElement, useEffect, useRef, useState } from 'react';
import ScreenWrapper from '../../components/ScreenWrapper/ScreenWrapper';
import withAuth from '../../auth/withAuth';
import { Button, Color, Components, Font, LoadingIndicator, Theme } from '@oneamerica/dxp-ui-components';
import {
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { getLayoutType, openPDF, formatCurrency } from '../../../utils';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons/faFilePdf';
import {
  FontAwesomeIcon,
  FontAwesomeIconStyle,
} from '@fortawesome/react-native-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import responsiveStyles from './styles';
import { useSelector, useDispatch } from 'react-redux';
import ErrorModal from '../../components/ErrorModal';
import {pensionVerification} from "../../config/languages/en.json";
import { trackCustomEvent, trackPage } from '../../../utils/Analytics/AdobeAnalyticsUtils';
import { analyticsTags } from '../../../utils/Analytics/AnalyticsTags';
import ColorConstants from '../../constants/ColorConstants';
import {
  getPensionVerificationList,
  getPensionVerification,
  postRegeneratePensionVerification,
  resetPensionPDF,
} from '../../redux/pensionVerification/action';

interface AccountForm {
  documentId?: number | string;
  paymentDescription?: string;
  amount?: string;
  frequency?: string;
}

type AccountForms = AccountForm[];

const REGENERATE_SUBMITTED_SESSION_KEY = 'pvlRegenerateSubmitted';

type PensionVerificationProps = {
  accessToken?: {
    accessToken?: string;
  };
};

const PensionVerification = (props: PensionVerificationProps) => {

  // Storing a key to track if regenerate button has already been pressed during the current session.
  // NOTE: Since we are not implementing a GET endpoint for the regenerate flag, this is the best we can do to reduce unessasary calls.
  const [isRegenerateSubmitted, setIsRegenerateSubmitted] = useState(() => {
    const sessionStore = (globalThis as any)?.sessionStorage;
    return sessionStore?.getItem(REGENERATE_SUBMITTED_SESSION_KEY) === 'true';
  });
  const hasInitiatedRegenerate = useRef(false);
  // Extract account from URL first, then fall back to localStorage
  //@ts-ignore
  const queryParams = globalThis.location?.search;
  const params = new URLSearchParams(queryParams);
  const urlAccount = params.get('account');
  const account = urlAccount || localStorage.getItem('planNumber');
  
  // Update localStorage if URL account differs from stored planNumber.
  useEffect(() => {
    if (urlAccount && urlAccount !== localStorage.getItem('planNumber')) {
      localStorage.setItem('planNumber', urlAccount);
    }
  }, [urlAccount]);

  const dispatch = useDispatch();
  const {
    getPensionVerificationListData: getPVLDocumentListData,
    getPensionVerificationData: getPVLDocumentData,
    isPensionVerificationListError: isPVLDocumentListError = false,
    isPensionVerificationError: isPVLDocumentError = false,
    isRegeneratePensionVerificationLoading: isRegenerateLoading = false,
    loading = true,
    error = {},
  } = useSelector((state: any) => state.pensionVerification);
   const accessToken = props?.accessToken?.accessToken;

  useEffect(() => {
    // Add analytics
    trackPage(analyticsTags.PVLPageViewTags);
    const isDataEmpty =!Array.isArray(getPVLDocumentListData) ||getPVLDocumentListData.length === 0;
    if (accessToken && isDataEmpty && account) {
      dispatch(getPensionVerificationList(account, accessToken));
    }
  }, [accessToken, account, getPVLDocumentListData, dispatch]);

   useEffect(() => {
     if (getPVLDocumentData) {
      openPDF(getPVLDocumentData, 'PensionVerification');
      dispatch(resetPensionPDF());
     }
  }, [getPVLDocumentData]);

    const styles = responsiveStyles();
    const layoutType = getLayoutType();
    const isDesktopOrTablet = layoutType === 'desktop' || layoutType === 'tablet';

  // Organize the data from the API response into structured variables.
  const isArray = Array.isArray(getPVLDocumentListData);
  let customerFullName: string | undefined;
  let PVLDocuments: AccountForms = []; 
  if (isArray) {
    const [customerData, documentData] = getPVLDocumentListData;
  
    customerFullName = customerData?.customerFullName;
    PVLDocuments = documentData?.pvlDocuments ?? [];
  }
  const NoPVLDocs = PVLDocuments.length === 0;
  const handleDocumentPress = (formDetails: AccountForm) => {  
    const {
      documentId,
      paymentDescription,
      amount,
      frequency
    } = formDetails;

    // Use the base64 data from formDetails to open PDF and create a user-friendly name for the file.
    const fileName = `PensionVerification_${paymentDescription}_${amount}_${frequency}.pdf`;
     trackCustomEvent(analyticsTags.PVLPDFClick(fileName));
     if (!accessToken || !documentId) return;

    dispatch(getPensionVerification(account, documentId, accessToken));
  };

  const onRegenerate = () => {
    if (isRegenerateSubmitted || isRegenerateLoading || hasInitiatedRegenerate.current) 
      return;
    if (account && accessToken) {
      trackCustomEvent(analyticsTags.PVLRegenerateClick);
      hasInitiatedRegenerate.current = true;
      dispatch(postRegeneratePensionVerification(account, accessToken));
    }
  };

  useEffect(() => {
    if (hasInitiatedRegenerate.current && !isRegenerateLoading) {
      const sessionStore = (globalThis as any)?.sessionStorage;
      sessionStore?.setItem(REGENERATE_SUBMITTED_SESSION_KEY, 'true');
      setIsRegenerateSubmitted(true);
    }
  }, [isRegenerateLoading]);

  const regenerateColor = isRegenerateSubmitted
    ? '#999999'
    : Theme.Color.oaBlue;

  const regenerateButtonLabelStyle = {
    color: regenerateColor,
        fontFamily: Font.semiBold.fontFamily,
      };

  const prepareTableData = (accountForms: AccountForms, layout: string) => {
    return accountForms.map((form: AccountForm) => {
      return [
        <Text key={`paymentDescription-${form.documentId}`} style={{ textAlign: 'left' }}>{form.paymentDescription}</Text>,
        <View key={`amount-${form.documentId}`} style={{flexDirection: 'row', flexWrap: 'wrap'}}>
          <Text>{formatCurrency(form.amount || '0') + "/"}</Text>
          <Text>{form.frequency}</Text>
        </View>,
        <TouchableOpacity
          key={`pdfIcon-${form.documentId}`}
          onPress={() => handleDocumentPress(form)}
          style={{
            justifyContent: 'center',
            padding: 2,
            borderWidth: 0, // Remove any border
          }}
          activeOpacity={0.7} // Smooth opacity change on press
          accessibilityRole="button"
          accessibilityLabel="Download PDF">
          <FontAwesomeIcon
            testID="pdfIcon"
            style={styles.faFilePdfIcon as FontAwesomeIconStyle}
            icon={faFilePdf as IconProp}
            color={Color.oaBlueLight}
          />
        </TouchableOpacity>,
      ];
    });
  };
  const centeredHeaderText = (label: string) => (
    <Text style={styles.headerTextCell} numberOfLines={1} ellipsizeMode="tail">
      {label}
    </Text>
  );
  const renderPVLTable = (
    accountForms: AccountForms,
    viewType: 'desktop' | 'tablet' | 'mobile' = 'desktop',
  ) => {
    const tableData = prepareTableData(accountForms, viewType);
    let wrapperStyle = styles.tableWrapper;
    let tableStyle = styles.tableStyle;
    let flexArr: number[] = [0.4, 0.4, 0.2];
    let headerColumns: (string | ReactElement)[] = [];
    let headerHeight: number;
    if (viewType === 'tablet') {
      wrapperStyle = styles.tabletTableWrapper;
      flexArr = [0.5, 0.5, 0.2];
      headerHeight = 35;
      headerColumns = [
        <View key="description-column" style={{ flex: 1 }}>
          <Text style={styles.headerTextCell}>{pensionVerification.paymentDescriptionHeader}</Text>
        </View>,
        <View key="amount-column" style={{ flex: 1 }}>
          <Text style={styles.headerTextCell}>{pensionVerification.AmountHeader}</Text>
        </View>,
        <View key="file-column" style={{ flex: 1}}>
          <Text style={styles.headerTextCell}>{pensionVerification.FileHeader}</Text>
        </View>,
      ];
    } else if (viewType === 'mobile') {
      wrapperStyle = styles.mobileTableWrapper;
      tableStyle = styles.mobileTableStyle;
      flexArr = [1, 1, .5];
      headerHeight = 50;
      headerColumns = [
        <View key="description-column" style={{ flex: 1 }}>
          <Text style={styles.headerTextCell}>{pensionVerification.paymentDescriptionHeader}</Text>
        </View>,
        <View key="amount-column" style={{ flex: 1 }}>
          <Text style={styles.headerTextCell}>{pensionVerification.AmountHeader}</Text>
        </View>,
        <View key="file-column" style={{ flex: 1 }}>
          <Text style={styles.headerTextCell}>{pensionVerification.FileHeader}</Text>
        </View>,
      ];
    } else {
        // Desktop
      headerHeight = 35;
      headerColumns = [
        centeredHeaderText(pensionVerification.paymentDescriptionHeader),
        centeredHeaderText(pensionVerification.AmountHeader),
        centeredHeaderText(pensionVerification.FileHeader),
      ];
    }
    return (
      <View style={wrapperStyle}>
        <Components.OATable
          tableHeight={tableData.length > 6 ? 200 : undefined}
          flexArr={flexArr}
          style={tableStyle}
          headerColumns={headerColumns}
          blueRows={true}
          tableData={tableData}
          hasNoData={tableData.length > 0}
          altRows={true}
          headerColor={ColorConstants.lightGrey}
          headerHeight={headerHeight}
        />
      </View>
    );
  };

  const getPVLDocumentsDescription = () => {
    if (error?.status === 404 || NoPVLDocs) {
    return (
      <View>
          <Text className='mt-2'>
            {pensionVerification.noPVLDocDesc}
          </Text>
        </View>
      );
    }
    else {
      return (
        <View>
          <Text className='mt-2 mb-2'>
            {pensionVerification.pensionVerificationDesc}
          </Text>
         </View>
       );
    }
  };

const renderHeader = () => (
    <View className='mb-2'>
      <Text style={styles.pensionVerificationTitle}>{pensionVerification.pensionVerificationHeader}</Text>
      <Components.Divider />
      {getPVLDocumentsDescription()}
    </View>
  );

const renderTablesLayout = () => {
  if (!PVLDocuments || PVLDocuments.length === 0) return null;
  return (
    <View style={styles.tablesContainer}>
      {isDesktopOrTablet ? (
        <View style={layoutType === 'desktop' ? styles.desktopTablesRow : styles.tabletTablesRow}>
          {renderPVLTable(PVLDocuments, layoutType)}
        </View>
      ) : (
        <View style={styles.mobileTablesColumn}>
          {renderPVLTable(PVLDocuments, 'mobile')}
        </View>
      )}
    </View>
  );
};
  let regenerateButtonKey = 'regenerate-default';
  if (isRegenerateLoading) {
    regenerateButtonKey = 'regenerate-loading';
  } else if (isRegenerateSubmitted) {
    regenerateButtonKey = 'regenerate-submitted';
  }
  const renderRegenerateButton = () => {
    if (!PVLDocuments || PVLDocuments.length === 0) return null;

    return (
      <View className='mt-5'>
        <Components.Button
          key={regenerateButtonKey}
          wrapper
          outline
          onPress={onRegenerate}
          loading={isRegenerateLoading}
          disabled={isRegenerateSubmitted}
          style={styles.regenerateButton}
          hoverStyle={styles.regenerateButtonHover}
          focusStyle={styles.regenerateButtonHover}
          disabledStyle={isRegenerateSubmitted ? styles.regenerateButtonDisabled : styles.regenerateButton}
          accessibilityRole='button'
          accessibilityLabel="Request RefreshedLetters Button">
          <Text style={regenerateButtonLabelStyle}>
            {pensionVerification.regenerateAllButtonLabel}
          </Text>
        </Components.Button>
      </View>
    );
  };

  const hasPVLError = isPVLDocumentListError || isPVLDocumentError;
const isNot404 = error?.status !== 404;

const showError = hasPVLError && isNot404;
  let content;

  if (loading && account) {
    content = (
      <LoadingIndicator loading={true} testID="loading-indicator" />
    );
  } else if (showError) {
    content = <ErrorModal />;
  } else {
    content = (
      <View style={{ paddingHorizontal: 8 }}>
        {customerFullName && (
          <View className="hidden lg:flex flex-col md:flex-row justify-between items-start md:items-center mt-5 mb-5 pr-4 md:pr-6">
            <Components.Text.H1
              style={{
                color: Theme.Color.oaBlue,
                marginBottom: 8,
                fontFamily: Font.bold.fontFamily,
              }}>
              {customerFullName || ' '}
            </Components.Text.H1>
          </View>
        )}
        <View style={layoutType === 'desktop' ? { maxWidth: '65%' } : {}}>
          <Components.Card
            testID="pvlDocumentsCard"
            type="default"
            orientation="vertical">
            {renderHeader()}
            {!NoPVLDocs && renderTablesLayout()}
          </Components.Card>
          {!NoPVLDocs && renderRegenerateButton()}
          <Text
            className={NoPVLDocs ? 'mb-5 mt-5' : 'mb-5 mt-3'}
            style={styles.descriptionText}>
            {NoPVLDocs
              ? pensionVerification.LegalDisclaimerText
              : pensionVerification.RegenerateButtonDesc}
          </Text>
        </View>
      </View>
    );
  }
  return <ScreenWrapper scrollFooter>{content}</ScreenWrapper>;
};

export default withAuth(PensionVerification);
